// ─────────────────────────────────────────────────────────────────────────────
// agentRunStore.ts — ONE AUTHOR OF "IS THIS AGENT RUNNING".
//
// OWNER RULING 2026-08-31: the same control on the composer, on the agent's
// dashboard and on the domain surface — "SAME CONTROL, SAME STATE, ONE AUTHOR —
// NOT A SECOND ANSWER TO 'IS THIS AGENT WORKING'."
//
// ── WHY A MODULE STORE AND NOT A CONTEXT ───────────────────────────────────
// Three mounts on three screens that never share a parent below <AppShell>. A
// provider would have to be threaded through App.tsx, whose Flag 1 contract puts
// ALL state there — so the provider would become a fourth owner of the same
// fact, which is the thing this store exists to prevent. A module store is
// mounted nowhere, so it cannot be mounted twice.
//
// AND IT IS THE CHEAP ANSWER TOO. `useSyncExternalStore` hands each subscriber
// its OWN key's entry, so a rail listing ten agents re-renders only the row
// whose agent changed — not the rail.
//
// ── THE POLL IS REFCOUNTED, SO N MOUNTS ARE STILL ONE READ ─────────────────
// The composer's strip used to own a 15s interval. Three mounts owning three
// intervals would be three times the load on a box the control exists to
// UNSTARVE — the fault in miniature. One interval, started by the first
// subscriber and stopped by the last.
//
// ── WHAT IS READ, AND WHAT IT COSTS (MEASURED 2026-08-31, IDLE BOX) ────────
//     GET /api/growth/runner          2.88 ms    armed · ruled · state · live
//     GET /api/rexy/agent-loop        4.10 ms    PERSONAL/spencer
//                                  2653.60 ms    GROWTH/buck  ← 1607 cascade rows
//
// So THE HARNESS AGENT IS READ FROM THE CHEAP ROUTE AND NEVER FROM THE OTHER
// ONE. `server/app.py` monkey-patches eventlet: that 2.65 s is a hub-blocking
// read on the single process Buck saturates, and firing it to render his own
// off-switch was adding to the starvation the switch is for. The expensive route
// is now reached ONLY BY A WRITE — a deliberate press, once.
//
// ── THE OFF PATH DOES NOT WAIT ON THE READ ─────────────────────────────────
// `stop()` takes no read first, needs no prior state and is callable while every
// read is failing. That is the owner's requirement in one line: "the OFF path
// must not itself require the poll to be answering". It is safe in that one
// direction only — the route's `warm()` and its Buck kick both sit inside
// `if enabled`, so a stop starts nothing and spends nothing. `arm()` keeps the
// old discipline and refuses without a read.
// ─────────────────────────────────────────────────────────────────────────────

import { getAgentLoop, setAgentLoop, AgentLoopState } from '../api/agentLoop';
import {
  getRunnerStatus, getRunnerStatusAt, DELTA_ACTIVITY_PATH, RunnerStatus,
} from '../api/growthRunner';
import { isBuckPair } from '../lib/buckLane';
import { ReadPhase, WritePhase } from '../lib/runControl';

/** How often the shared read repeats. The runner route is read-only, reaches no
 *  network and spends nothing — its own docstring says so — so this is a local
 *  poll, not a cost decision. */
export const RUN_POLL_MS = 15000;

/**
 * A FAILED READ RETRIES, AND IT DOES NOT WAIT 15 SECONDS TO DO IT.
 *
 * Owner ruling 2026-08-31: when the state genuinely cannot be read the control
 * says so in four words AND IT RETRIES. The steady poll is a heartbeat for a
 * route that is answering; it is the wrong cadence for one that just failed,
 * because the owner is standing there NOW.
 *
 * 1s, 2s, 4s, then the ordinary poll takes over. The route costs 2.88 ms and
 * reaches no network, so three extra reads in seven seconds is nothing — this
 * is a responsiveness decision, not a cost one.
 */
export const RETRY_BACKOFF_MS = [1000, 2000, 4000];

/**
 * HOW OFTEN THE SHARED READ REPEATS WHILE HE IS STOPPING.
 *
 * `stopping` is a TRANSITIONAL state — disarmed, round in flight, resolving to
 * `off` on its own within a few minutes — and it is the one state the owner is
 * DEFINITELY standing in front of, because he just pressed the button that
 * caused it. On the ordinary 15 s heartbeat the dot would go on saying STOPPING
 * for up to fifteen seconds after he had actually stopped: the same lie this
 * build is a correction of, in miniature, at the exact moment he is watching.
 *
 * Same reasoning as the retry ladder above and the same shape: the route costs
 * 2.88 ms, reaches no network and spends nothing, so ~5 extra reads per stop is
 * a responsiveness decision rather than a cost one.
 */
export const STOPPING_POLL_MS = 3000;

/**
 * HOW MANY CONSECUTIVE FAILURES BEFORE THE CONTROL STOPS SAYING "RETRYING".
 *
 * At this point the refusal would become PERMANENT, and the owner ruled that
 * out by name: "the owner cannot turn Buck ON from the screen at all. He can
 * only stop something he cannot see. THE ON SWITCH IS STILL AN SSH COMMAND."
 * So the control stops waiting and offers BOTH actions — see `resolveRunControl`.
 */
export const RETRIES_BEFORE_OFFERING = RETRY_BACKOFF_MS.length;

/**
 * HOW LONG A WRITE MAY GO UNANSWERED BEFORE THE CONTROL SAYS SO.
 *
 * Not a timeout: the request is NOT cancelled and a late reply is still applied.
 * It is the point at which "…" becomes a lie of omission — the owner has been
 * staring at a spinner and deserves the third state. Six seconds because the
 * measured worst case for the route's own capability read is 2.65 s on an IDLE
 * box, so anything under ~5 s would fire on a healthy write.
 */
export const WRITE_UNCONFIRMED_MS = 6000;

/**
 * HOW LONG A PER-AGENT READ STAYS GOOD.
 *
 * A store that cached forever would be the staleness fault this build is
 * otherwise closing: arm an agent on his dashboard, walk to his chat, and the
 * chip would still show what it read before the write — one author, one ANSWER,
 * and the answer wrong. A store that never cached would be the old behaviour,
 * one read per mount, which is the cost this build is closing.
 *
 * Thirty seconds is the compromise, and it costs nothing where it matters: the
 * HARNESS agent is not on this path at all — his state rides the 15 s shared
 * poll — so this only ever governs the cheap reads (4.10 ms, measured).
 */
export const READ_STALE_MS = 30000;

export interface AgentRunEntry {
  domain: string;
  agent: string;
  loop: AgentLoopState | null;
  read: ReadPhase;
  write: WritePhase;
  writeWanted: boolean | null;
  /** When `read` last became 'read'. 0 ⇒ never. See READ_STALE_MS. */
  readAt: number;
  /**
   * Consecutive failed reads FOR THIS AGENT. Zero on any success.
   *
   * THE HARNESS AGENT IS NOT THE ONLY ONE WHOSE READ CAN FAIL, and the first
   * version of the retry ladder forgot that: it counted only the shared runner
   * route, so a generic agent whose toggle read failed sat on UNREAD · RETRYING
   * for ever — the permanent refusal the owner ruled out, moved off Buck and
   * onto the other nine. Every read retries.
   */
  failures: number;
}

/** The one definition of "does this agent have a work harness". Buck alone
 *  today; it takes the PAIR, never the agent alone. */
export function isHarnessAgent(domain: unknown, agent: unknown): boolean {
  return isBuckPair(domain, agent);
}

/** The store's key. Matches the backend's own normalisation — domain upper,
 *  agent lower — so BUCK, Buck and buck are one agent here as they are there. */
export function runKey(domain: unknown, agent: unknown): string {
  return `${String(domain ?? '').trim().toUpperCase()}/${String(agent ?? '').trim().toLowerCase()}`;
}

function blankEntry(domain: string, agent: string): AgentRunEntry {
  return {
    domain, agent, loop: null, read: 'unread', write: 'idle', writeWanted: null,
    readAt: 0, failures: 0,
  };
}

// ── STATE ───────────────────────────────────────────────────────────────────
let entries: Record<string, AgentRunEntry> = {};

/**
 * ONE SHARED READ OF ONE "IS HE WORKING" ROUTE — refcounted poll, retry ladder,
 * the faster cadence while `stopping`. Buck's was the only one; DELTA'S LIGHT
 * (owner ruling 2026-09-23) is "THE SAME MECHANISM AND THE SAME MEANING — not a
 * second, different one built beside it", so the mechanism became a value and
 * each agent with a route gets an INSTANCE of it. Same constants, same code
 * path, same `normalizeRunner`, same dot.
 */
export interface SharedRead {
  readonly name: string;
  readonly fetch: () => Promise<RunnerStatus>;
  runner: RunnerStatus | null;
  read: ReadPhase;
  /** Consecutive failed reads. Zero on any success. */
  failures: number;
  reading: boolean;
  pollTimer: ReturnType<typeof setInterval> | null;
  pollRefs: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
  /** The faster re-read held only while the route says `stopping`. */
  stoppingTimer: ReturnType<typeof setTimeout> | null;
}

function sharedRead(name: string, fetch: () => Promise<RunnerStatus>): SharedRead {
  return {
    name, fetch, runner: null, read: 'unread', failures: 0, reading: false,
    pollTimer: null, pollRefs: 0, retryTimer: null, stoppingTimer: null,
  };
}

/** Buck — `/api/growth/runner`. Called through `getRunnerStatus` by name, so a
 *  bar that mocks that function still drives him. */
const BUCK_READ = sharedRead('buck', () => getRunnerStatus());
/** Delta — `/api/delta/activity`, the same shape from his own producer. */
const DELTA_READ = sharedRead('delta', () => getRunnerStatusAt(DELTA_ACTIVITY_PATH));

/**
 * WHICH SHARED READ ANSWERS "IS THIS AGENT WORKING", or null for an agent no
 * route answers for. Deliberately NOT `isHarnessAgent`: that one also decides
 * who gets Buck's RUN/STOP control, and Delta's light is a reading, not a switch.
 */
export function runnerSourceFor(domain: unknown, agent: unknown): SharedRead | null {
  if (isBuckPair(domain, agent)) return BUCK_READ;
  if (String(domain ?? '').trim().toUpperCase() === 'GROWTH'
      && String(agent ?? '').trim().toLowerCase() === 'delta') return DELTA_READ;
  return null;
}

/** STABLE BY REFERENCE between changes, as `useSyncExternalStore` requires. */
export function getRunnerOf(src: SharedRead | null): RunnerStatus | null {
  return src ? src.runner : null;
}
export function getRunnerFailuresOf(src: SharedRead | null): number {
  return src ? src.failures : 0;
}

const listeners = new Set<() => void>();
/** In-flight reads, so N mounts of one agent make ONE request. */
const reading = new Set<string>();
/** Per-agent backoff timers, so a retry can be cancelled with the store. */
const entryRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();

function emit(): void {
  listeners.forEach((fn) => { try { fn(); } catch { /* a bad subscriber is not the store's problem */ } });
}

function patch(key: string, next: Partial<AgentRunEntry>): void {
  const cur = entries[key];
  if (!cur) return;
  // A NEW OBJECT for the changed key and the SAME object for every other one,
  // so `useSyncExternalStore` re-renders exactly the subscribers that moved.
  entries = { ...entries, [key]: { ...cur, ...next } };
  emit();
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The entry for one agent. STABLE BY REFERENCE between changes — required by
 *  `useSyncExternalStore`, which loops forever on a fresh object each call. */
export function getEntry(domain: string, agent: string): AgentRunEntry {
  const key = runKey(domain, agent);
  if (!entries[key]) {
    entries = { ...entries, [key]: blankEntry(domain, agent) };
  }
  return entries[key];
}

export function getRunner(): RunnerStatus | null { return BUCK_READ.runner; }
export function getRunnerRead(): ReadPhase { return BUCK_READ.read; }
/** Consecutive failures. The view reads it to say RETRYING, and to know when
 *  waiting has stopped being honest. */
export function getRunnerFailures(): number { return BUCK_READ.failures; }

// ── THE SHARED RUNNER READ — ONE INTERVAL FOR THE WHOLE APP ────────────────
function scheduleRetry(src: SharedRead): void {
  if (src.retryTimer !== null) return;
  // Past the end of the ladder the ordinary poll is the retry. Scheduling
  // another timer there would be a second interval on the same read — the
  // refcount fault this store exists to prevent, wearing a backoff.
  const delay = RETRY_BACKOFF_MS[src.failures - 1];
  if (delay === undefined) return;
  src.retryTimer = setTimeout(() => { src.retryTimer = null; readShared(src); }, delay);
}

/**
 * Tighten the cadence while he is stopping, and let go the moment he is not.
 *
 * READS THE ROUTE'S OWN WORD — never `!armed && running`, which would be the
 * second copy of the route's rule that `growthRunner.readState` refuses by
 * name. Nothing emits `stopping` yet, so this is inert until Forge's word
 * lands, and the ordinary heartbeat is the behaviour until then.
 *
 * Never runs when the poll is not held: a timer outliving the last mount is the
 * orphaned-interval fault the refcount exists to prevent, and this is a second
 * timer on the same read.
 */
function scheduleStoppingPoll(src: SharedRead): void {
  const stopping = src.runner?.reachable === true && src.runner.state === 'stopping';
  if (!stopping || src.pollTimer === null) {
    if (src.stoppingTimer !== null) { clearTimeout(src.stoppingTimer); src.stoppingTimer = null; }
    return;
  }
  if (src.stoppingTimer !== null) return;
  src.stoppingTimer = setTimeout(() => { src.stoppingTimer = null; readShared(src); }, STOPPING_POLL_MS);
}

/** Buck's read, by its old name — the write path re-reads him after a press. */
function readRunner(): void { readShared(BUCK_READ); }

function readShared(src: SharedRead): void {
  if (src.reading) return;
  src.reading = true;
  src.read = src.read === 'read' ? 'read' : 'reading';
  emit();
  // Through `then`, so a fetcher that throws synchronously is a failed read too.
  Promise.resolve()
    .then(() => src.fetch())
    .then((r) => {
      src.runner = r;
      // `getRunnerStatus` never rejects — an unread route comes back as an
      // UNREACHABLE status rather than an exception, so `reachable` is what
      // separates "read it" from "did not".
      src.read = r.reachable ? 'read' : 'failed';
      if (r.reachable) src.failures = 0;
      else { src.failures += 1; scheduleRetry(src); }
      scheduleStoppingPoll(src);
    })
    .catch(() => {
      src.runner = null; src.read = 'failed';
      src.failures += 1; scheduleRetry(src);
      // The runner is gone, so there is no `stopping` to chase. Releases the
      // faster cadence rather than leaving it running against a dead route.
      scheduleStoppingPoll(src);
    })
    .finally(() => { src.reading = false; emit(); });
}

/** Start the shared poll. Returns the release; the interval stops when the last
 *  holder releases it. Idempotent per caller. */
export function holdRunnerPoll(): () => void {
  return holdRunnerPollOf(BUCK_READ);
}

/** The same hold on any shared read. */
export function holdRunnerPollOf(src: SharedRead): () => void {
  src.pollRefs += 1;
  if (src.pollTimer === null) {
    readShared(src);
    src.pollTimer = setInterval(() => readShared(src), RUN_POLL_MS);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    src.pollRefs -= 1;
    if (src.pollRefs <= 0 && src.pollTimer !== null) {
      clearInterval(src.pollTimer);
      src.pollTimer = null;
      src.pollRefs = 0;
      // The retry ladder is part of the same read, so it is released with it.
      // A backoff timer outliving the last mount is the orphaned-interval fault
      // in a second timer.
      if (src.retryTimer !== null) { clearTimeout(src.retryTimer); src.retryTimer = null; }
      if (src.stoppingTimer !== null) { clearTimeout(src.stoppingTimer); src.stoppingTimer = null; }
    }
  };
}

// ── THE PER-AGENT READ ──────────────────────────────────────────────────────
/**
 * Read one agent's toggle, ONCE, deduped.
 *
 * THE HARNESS AGENT IS NOT READ HERE AT ALL and that is the point of the whole
 * module: his state comes from the 2.88 ms shared route, so his surfaces never
 * fire the 2653 ms one. Calling this for him is a no-op rather than an error —
 * a caller should not have to know which agent it is holding.
 */
export function ensureLoop(domain: string, agent: string): void {
  if (!domain || !agent) return;
  if (isHarnessAgent(domain, agent)) return;
  const key = runKey(domain, agent);
  getEntry(domain, agent);
  const cur = entries[key];
  // Never re-read over a write, and never stack reads.
  if (reading.has(key) || cur.read === 'reading' || cur.write !== 'idle') return;
  // A GOOD READ IS KEPT; A STALE ONE IS REFRESHED. The cached value stays on
  // screen while the refresh is in flight — replacing it with 'reading' would
  // flick a correct answer to UNREAD for the length of a round trip, which on a
  // starved box is exactly when it must not.
  if (cur.read === 'read' && Date.now() - cur.readAt < READ_STALE_MS) return;
  // A retry already scheduled is a read already coming.
  if (entryRetryTimers.has(key)) return;
  reading.add(key);
  // Only a FIRST read announces itself as 'reading'. A refresh over a good value
  // leaves the value in place — see the staleness note above.
  if (cur.read !== 'read') patch(key, { read: 'reading' });
  getAgentLoop(domain, agent)
    .then((s) => { patch(key, { loop: s, read: 'read', readAt: Date.now(), failures: 0 }); })
    .catch(() => {
      const failures = (entries[key]?.failures ?? 0) + 1;
      patch(key, { loop: null, read: 'failed', readAt: 0, failures });
      // THE SAME LADDER THE SHARED READ USES. One rule for both reads, so a
      // generic agent's control resolves on the same terms Buck's does.
      const delay = RETRY_BACKOFF_MS[failures - 1];
      if (delay !== undefined) {
        const t = setTimeout(() => {
          entryRetryTimers.delete(key);
          ensureLoop(domain, agent);
        }, delay);
        entryRetryTimers.set(key, t);
      }
    })
    .finally(() => { reading.delete(key); });
}

/** Drop a cached read so the next `ensureLoop` goes to the wire. */
export function invalidate(domain: string, agent: string): void {
  const key = runKey(domain, agent);
  if (!entries[key]) return;
  patch(key, { read: 'unread', loop: null, readAt: 0, failures: 0 });
}

// ── THE WRITE ───────────────────────────────────────────────────────────────
function write(domain: string, agent: string, enabled: boolean): Promise<void> {
  const key = runKey(domain, agent);
  getEntry(domain, agent);
  if (entries[key].write === 'sending') return Promise.resolve();

  patch(key, { write: 'sending', writeWanted: enabled });

  let settled = false;
  const late = setTimeout(() => {
    // NOT A CANCEL. The request is still running and a late reply still lands;
    // this only stops the control from claiming to still be mid-press when it
    // has no idea. See WRITE_UNCONFIRMED_MS.
    if (!settled) patch(key, { write: 'unconfirmed' });
  }, WRITE_UNCONFIRMED_MS);

  const done = () => { settled = true; clearTimeout(late); };

  return setAgentLoop(domain, agent, enabled)
    .then((s) => {
      done();
      // THE CONTROL MOVES ON WHAT CAME BACK, NEVER ON WHAT WAS ASKED FOR. The
      // route returns the EFFECTIVE state; `enabled` is not consulted here.
      patch(key, { loop: s, read: 'read', readAt: Date.now(), write: 'idle', writeWanted: null });
    })
    .catch(() => {
      done();
      // A FAILED WRITE RE-READS. It never keeps the value that was clicked —
      // and for the harness agent the re-read is the cheap route, so a failed
      // stop does not cost 2.65 s to find out it failed.
      patch(key, { write: 'idle', writeWanted: null, read: 'unread', loop: null, readAt: 0 });
      if (isHarnessAgent(domain, agent)) readRunner();
      else ensureLoop(domain, agent);
    })
    .finally(() => {
      // The consent moved (or we no longer know), so the shared strip is stale.
      if (isHarnessAgent(domain, agent)) readRunner();
    });
}

/**
 * THE OFF PATH. Takes no read first, needs no prior state, and is callable while
 * every read on the box is failing.
 *
 * SAFE IN THIS DIRECTION ONLY, and the reason is in `server/app.py`: both
 * `loop_binding.warm()` and the Buck kick sit inside `if body["enabled"]`, so a
 * stop imports nothing, starts nothing and spends nothing. Its worst case is an
 * agent that was already off being told so again.
 */
export function stop(domain: string, agent: string): Promise<void> {
  return write(domain, agent, false);
}

/** THE ON PATH. Callers gate it on `resolveArmable` — the store does not arm an
 *  agent it has not read, because that is inventing an ON. */
export function arm(domain: string, agent: string): Promise<void> {
  return write(domain, agent, true);
}

/** Test seam. NOT a production handle — it exists so a bar can start from a
 *  known store, and nothing in `src/components` may call it. */
export function __resetForTests(): void {
  entries = {};
  reading.clear();
  entryRetryTimers.forEach((t) => clearTimeout(t));
  entryRetryTimers.clear();
  listeners.clear();
  [BUCK_READ, DELTA_READ].forEach((src) => {
    if (src.pollTimer !== null) clearInterval(src.pollTimer);
    if (src.retryTimer !== null) clearTimeout(src.retryTimer);
    if (src.stoppingTimer !== null) clearTimeout(src.stoppingTimer);
    Object.assign(src, {
      runner: null, read: 'unread', failures: 0, reading: false,
      pollTimer: null, pollRefs: 0, retryTimer: null, stoppingTimer: null,
    });
  });
}
